import React, { useContext, useEffect, useMemo, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Breadcrumbs, Container, Link, Typography } from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';
import _get from 'lodash/get';

import ContractDebtCard from '../Cards/ContractDebtCard';
import CurrentDebtCard from '../Cards/CurrentDebtCard/CurrentDebtCard';
import DeferredDebtCard from '../Cards/DeferredDebtCard/DeferredDebtCard';
import PunishedDebtCard from '../Cards/PunishedDebtCard/DeferredDebtCard';
import ModalProgress from '../../../Components/Progress/Modal/ModalProgress';

import {
  ConstructorContractsContext,
  ContractSelectedContext,
  ContractsContext,
  OnContractChangeContext
} from '../../../Contexts/ContractsContext';
import {
  UserContext,
  UserDispatchContext
} from '../../../Contexts/UserContext';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';

import {
  redirectOnAuthFailure,
  extractErrorMessage
} from '../../../Utils/Errors/Errors';

import { ROUTE_NAMES } from '../../../Routes/Routes';
import { history } from '../../../Routes/history';
import {
  GetContractDebtsAPI,
  GetDebtNegotiationPlanAPI
} from '../../../API/Debts/DebtsAPI';
import { GetInvoicesAPI } from '../../../API/Invoices/InvoicesAPI';

import myDebtsBanner from '../../../Assets/images/my-debts-banner.png';
import SweetAlert from '../../../Components/Alerts/SweetAlert';

import { getCurrentDebtData } from '../helpers/helpers';
import { ALERT_TYPE } from '../../../Components/Alerts/alert_enums';
import { Company, PortalCheck } from '../../../Configs/general';
import { getIsBillingPeriod } from '../helpers/helpers';
import { logoutUser } from '../../../Utils/User/Actions';
import { checkCorporateContract } from '../../../Utils/Contract/checkCorporateContract';

const CARD_TYPES = {
  current: 'current',
  deferred: 'deferred',
  punished: 'punished'
};

const MyDebtsGDC = (props = { match: { params: {} } }) => {
  const {
    match: {
      params: { contractId: paramsContractId }
    }
  } = props;

  const classes = useStyles();

  // * CONTEXTS
  const contracts = useContext(ContractsContext);
  const constructorContracts = useContext(ConstructorContractsContext);
  const selectedContract = useContext(ContractSelectedContext);
  const setOnContractChange = useContext(OnContractChangeContext);
  const currentUser = useContext(UserContext);
  const setAlert = useContext(AlertsDispatchContext);
  const setCurrentUser = useContext(UserDispatchContext);

  const authToken = _get(currentUser, 'token');

  // * STATE HOOKS
  const [contract, setContract] = useState(null);
  const [debtsData, setDebtsData] = useState(null);
  const [invoicesData, setInvoicesData] = useState(null);
  const [negotiationData, setNegotiationData] = useState(null);
  const [loading, setLoading] = useState(false);

  // * GLOBAL VARS

  const isCorporateContract = useMemo(
    () =>
      checkCorporateContract(selectedContract ? selectedContract.type : null),
    [selectedContract]
  );

  const currentDebtData =
    debtsData && getCurrentDebtData(debtsData, negotiationData);

  const punishedData = debtsData && debtsData.punishedData;

  const punishedProductIds =
    punishedData &&
    punishedData.products.map(punishedProduct => punishedProduct.productId);

  const isBillingPeriod =
    debtsData &&
    getIsBillingPeriod(debtsData.daysUntilBilling, debtsData.billingDate);

  // * FUNCTIONS

  useEffect(() => {
    if (!paramsContractId) {
      setContract(selectedContract);
      return;
    }

    const constructorContractId = parseInt(paramsContractId, 10);

    if (Number.isNaN(constructorContractId)) {
      history.push(ROUTE_NAMES.constructors);
      return;
    }

    const constructorContract =
      constructorContracts &&
      constructorContracts.find(c => c.id === constructorContractId);

    if (!constructorContract) {
      history.push(ROUTE_NAMES.constructors);
      return;
    }

    setContract({ ...constructorContract, isConstructor: true });

    //Since constructor contracts are not selected, we have to
    //redirect to regular debt status in case a client selects
    //another contract and do so once for avoiding unexpected behavior
    setOnContractChange.current = () => {
      history.push(ROUTE_NAMES.debtStatus);
      setOnContractChange.current = null;
    };

    return () => {
      setOnContractChange.current = null;
    };
  }, [
    paramsContractId,
    constructorContracts,
    selectedContract,
    setOnContractChange
  ]);

  useEffect(() => {
    if (!authToken) {
      history.replace('/');
    }

    if (
      (!contracts || !contracts.length) &&
      (!constructorContracts || !constructorContracts.length)
    ) {
      history.replace(ROUTE_NAMES.contracts);
    }

    if (!contract) {
      return;
    }

    let isMounted = true;

    const fetchDetbs = async () => {
      const debtsResponse = await GetContractDebtsAPI(authToken, contract.id);

      if (!isMounted) {
        return;
      }

      if (!debtsResponse.success) {
        setDebtsData(null);

        if (
          redirectOnAuthFailure(debtsResponse, '/', () =>
            logoutUser(setCurrentUser)
          )
        ) {
          return;
        }

        setAlert({
          type: 'error',
          message: extractErrorMessage(debtsResponse).message
        });
        return;
      }

      const responseData = debtsResponse.data.data;

      setDebtsData(responseData);
    };

    const fetchInvoices = async () => {
      const invoicesResponse = await GetInvoicesAPI(
        contract.id,
        null,
        authToken
      );

      if (!isMounted) {
        return;
      }

      if (!invoicesResponse.success) {
        setInvoicesData(null);
        return;
      }

      const responseData = invoicesResponse.data.data;
      setInvoicesData(responseData);
    };

    const fetchNegotiation = async () => {
      const negotiationResponse = await GetDebtNegotiationPlanAPI(
        authToken,
        contract.id
      );

      if (!isMounted) {
        return;
      }

      if (!negotiationResponse.success) {
        setNegotiationData(null);
        return;
      }

      const responseData = negotiationResponse.data.data;
      const usuryRate = Number.parseFloat(responseData.usuryRate / 12).toFixed(
        2
      );

      setNegotiationData({ ...responseData, usuryRate });
    };

    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchDetbs(), fetchInvoices(), fetchNegotiation()]);
      if (isMounted) {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    authToken,
    contracts,
    constructorContracts,
    selectedContract,
    contract,
    setAlert,
    setCurrentUser
  ]);

  const getCard = type => {
    switch (type) {
      case CARD_TYPES.current:
        return (
          <CurrentDebtCard
            debts={currentDebtData}
            invoices={invoicesData}
            selectedContract={contract}
            authToken={authToken}
            punishedProductIds={punishedProductIds}
            isBillingPeriod={isBillingPeriod}
          />
        );
      case CARD_TYPES.deferred:
        return (
          <DeferredDebtCard
            debts={debtsData}
            selectedContract={contract}
            isBillingPeriod={isBillingPeriod}
          />
        );
      case CARD_TYPES.punished:
        return (
          <PunishedDebtCard
            debts={currentDebtData}
            selectedContract={contract}
            isBillingPeriod={isBillingPeriod}
            negotiationData={negotiationData}
            punishedProductIds={punishedProductIds}
          />
        );
      default:
        return <></>;
    }
  };

  const getCardsOrder = () => {
    const debtsWithBalance = [];
    const debtsWithoutBalance = [];

    const { currentValue, deferredValue, punishedValue } = debtsData;

    if (currentValue === 0) {
      debtsWithoutBalance.push(getCard(CARD_TYPES.current));
    } else {
      debtsWithBalance.push(getCard(CARD_TYPES.current));
    }

    if (deferredValue === 0) {
      debtsWithoutBalance.push(getCard(CARD_TYPES.deferred));
    } else {
      debtsWithBalance.push(getCard(CARD_TYPES.deferred));
    }

    if (punishedValue > 0) {
      debtsWithBalance.push(getCard(CARD_TYPES.punished));
    }

    return debtsWithBalance.concat(debtsWithoutBalance);
  };

  if (loading) {
    return <ModalProgress message="Consultando tus deudas" />;
  }

  if (!debtsData) {
    return <></>;
  }

  return (
    <>
      <div className={classes.root}>
        <Container className={classes.container}>
          <Breadcrumbs
            aria-label="breadcrumb"
            className={classes.breadcrumbs}
            separator={<NavigateNextIcon fontSize="small" />}
          >
            <Link
              color="textPrimary"
              component={RouterLink}
              to="/"
              underline="always"
            >
              Inicio{' '}
            </Link>
            <Typography color="primary" className={classes.breadcrumbs}>
              Mis deudas
            </Typography>
          </Breadcrumbs>

          {negotiationData &&
            !isCorporateContract &&
            !isBillingPeriod &&
            !PortalCheck.isEfigas && (
              <SweetAlert
                type={ALERT_TYPE.INFO}
                message={
                  <>
                    Puedes realizar un <b>acuerdo de pago</b> para tu{' '}
                    {Company.contractConjugation.regular.singular.main}{' '}
                    <b>
                      {contract.id} ({contract.alias})
                    </b>
                    .
                  </>
                }
                hasAction
                actionText="Ir a acuerdo de pago"
                actionOnClick={() =>
                  history.push(ROUTE_NAMES.refinancingCurrent, {
                    selectedContract: contract,
                    data: currentDebtData,
                    punishedIds: punishedProductIds
                  })
                }
              />
            )}

          {isBillingPeriod && (
            <SweetAlert
              iconSize={20}
              type={ALERT_TYPE.WARNING}
              classes={{
                root: classes.sweetAlert,
                message: classes.sweetAlertText,
                messageContainer: classes.sweetAlertMessageContainer,
                icon: classes.sweetAlertIcon
              }}
              message={
                <>
                  <b>
                    Tu {Company.contractConjugation.regular.singular.main} se
                    encuentra en periodo de facturación, por lo que solo puedes
                    realizar las siguientes acciones:
                    <br />
                  </b>
                  - Descargar comprobante del estado de cuenta <br />
                  - Pagar deuda actual <br />
                  - Ver detalle de tu deuda actual o financiada <br />
                  - Abonar a Brilla <br />
                </>
              }
            />
          )}

          <ContractDebtCard
            contract={contract}
            title="Mis deudas"
            banner={myDebtsBanner}
            data={getCurrentDebtData(debtsData, negotiationData)}
          />
          {getCardsOrder().map((card, index) => (
            <div key={index}>{card}</div>
          ))}
        </Container>
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  container: {
    flex: 1,
    maxWidth: 816,
    marginTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginTop: theme.spacing(3)
    },
    '& > *': {
      marginBottom: theme.spacing(3)
    },
    '& > *:last-child': {
      marginBottom: 0,
      [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        marginBottom: theme.spacing(5)
      }
    }
  },
  breadcrumbs: {
    fontSize: 12,
    fontWeight: theme.typography.fontWeightMedium,
    '& > *': { fontSize: 'inherit' }
  },
  modalBackground: {
    backgroundColor: '#aaaaaa',
    opacity: '20%',
    height: '100%',
    width: '100%'
  },
  sweetAlertText: {
    fontSize: 12
  },
  sweetAlertMessageContainer: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  sweetAlertIcon: {
    marginTop: theme.spacing(0.5)
  }
}));

export default MyDebtsGDC;
